/* eslint-disable react/jsx-props-no-spreading */
import { usePostRoles } from 'api/payments/generated/fragmentPaymentsAPI';
import { useToast } from 'components/ToastManager';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import { Toast } from '@fragment/ui/src/components/Toast';

type FormData = {
  role: string;
};

type CreateRoleModalProps = {
  onClose: () => void;
  onCreated: (roleName: string) => void;
};

export const CreateRoleModal = ({
  onClose,
  onCreated,
}: CreateRoleModalProps) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const postRoleMutation = usePostRoles();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: { role: '' },
  });

  const onSubmit = useCallback(
    async (formData: FormData) => {
      try {
        await postRoleMutation.mutateAsync({
          data: { role: formData.role.trim() },
        });
        await queryClient.invalidateQueries({ queryKey: ['/roles'] });
        showToast(<Toast type="success" message="Role created successfully" />);
        onCreated(formData.role.trim());
        onClose();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to create role. Please try again.';
        showToast(
          <Toast
            type="error"
            message={`Failed to create role: ${errorMessage}`}
          />
        );
      }
    },
    [postRoleMutation, queryClient, showToast, onCreated, onClose]
  );

  const canSubmit = isValid && !postRoleMutation.isPending;

  return (
    <Modal
      data-testid="create-role-modal"
      onClose={onClose}
      width="sm"
      onSubmit={handleSubmit(onSubmit)}
      header={<div className="text-main">Create Role</div>}
      body={
        <div className="flex flex-col space-y-f2">
          <ModalField
            hasError={!!errors.role}
            label="Role Name"
            labelWidth="w-f12"
          >
            <ModalTextInput
              data-testid="role-name-input"
              placeholder="Role name"
              {...register('role', {
                required: 'Role name is required',
                validate: (value) =>
                  value.trim() !== '' || 'Role name cannot be empty',
              })}
            />
          </ModalField>
        </div>
      }
      footer={
        <div className="flex flex-row justify-between w-full">
          <FormButtons
            type="secondary"
            cancelButtonLabel="Cancel"
            submitButtonLabel={
              postRoleMutation.isPending ? 'Creating...' : 'Create'
            }
            onCancel={onClose}
            onSubmit={handleSubmit(onSubmit)}
            disabled={!canSubmit}
          />
        </div>
      }
    />
  );
};
